import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Copy, Edit, FileDown, FileText, Plus, Search } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  TableColumnVisibilityMenu,
  initialColumnVisibility,
  type TableColumnMenuItem,
} from '@/components/shared/TableColumnVisibilityMenu'
import { useAuthStore } from '@/store/auth.store'
import { salesOrderService } from '@/services/salesOrderService'

import type { SalesOrderRow } from '../types'

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const

const SALES_ORDER_LIST_COLUMNS: TableColumnMenuItem[] = [
  { id: 'code', label: 'Code' },
  { id: 'reference', label: 'Reference' },
  { id: 'warehouse', label: 'Warehouse' },
  { id: 'orderDate', label: 'Order date' },
  { id: 'expectedDeliveryDate', label: 'Expected delivery date' },
  { id: 'total', label: 'Total' },
  { id: 'edit', label: 'Edit' },
]

export const InventorySalesOrdersList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const branchId = useAuthStore((s) => s.user?.selectedBranch?.branchId)

  const [rows, setRows] = useState<SalesOrderRow[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [columnVisibility, setColumnVisibility] = useState(() =>
    initialColumnVisibility(SALES_ORDER_LIST_COLUMNS.map((c) => c.id)),
  )

  const visibleColumnCount = useMemo(
    () => SALES_ORDER_LIST_COLUMNS.filter((c) => columnVisibility[c.id]).length,
    [columnVisibility],
  )

  const toggleColumn = (columnId: string) => {
    setColumnVisibility((prev) => ({ ...prev, [columnId]: !prev[columnId] }))
  }

  useEffect(() => {
    setPageNumber(1)
  }, [branchId])

  useEffect(() => {
    if (!branchId) {
      setRows([])
      setTotalCount(0)
      return
    }
    const load = async () => {
      try {
        setLoading(true)
        const res = await salesOrderService.getAllSalesOrders(branchId, pageSize, pageNumber)
        setRows(res.items)
        setTotalCount(res.totalCount)
      } catch (error: any) {
        setRows([])
        setTotalCount(0)
        toast.error(error?.response?.data?.message || 'Failed to load sales orders.')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [branchId, pageNumber, pageSize])

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  useEffect(() => {
    if (pageNumber > totalPages) setPageNumber(totalPages)
  }, [pageNumber, totalPages])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) =>
      [r.code, r.reference, r.warehouse].some((x) => String(x).toLowerCase().includes(q)),
    )
  }, [rows, searchQuery])

  const fmtDate = (value?: string) => {
    if (!value) return '-'
    const d = new Date(value.includes('T') ? value : `${value}T12:00:00`)
    if (Number.isNaN(d.getTime())) return value
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  const fmtTotal = (n: number) => (Number.isFinite(n) ? n.toFixed(2) : String(n))

  const handleCopy = () => {
    if (filtered.length === 0) return toast.error('Nothing to copy.')
    const header = ['Code', 'Reference', 'Warehouse', 'Order date', 'Expected delivery date', 'Total']
    const lines = filtered.map((r) =>
      [r.code, r.reference, r.warehouse, r.orderDate, r.expectedDeliveryDate, fmtTotal(r.total)].join('\t'),
    )
    void navigator.clipboard.writeText([header.join('\t'), ...lines].join('\n'))
    toast.success('Copied.')
  }

  const handleExportCSV = () => {
    if (filtered.length === 0) return toast.error('Nothing to export.')
    const header = 'Code,Reference,Warehouse,OrderDate,ExpectedDeliveryDate,Total'
    const body = filtered
      .map((r) =>
        [r.code, r.reference, r.warehouse, r.orderDate, r.expectedDeliveryDate, fmtTotal(r.total)]
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(','),
      )
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `sales_orders_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success('CSV downloaded.')
  }

  const startIdx = totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1
  const endIdx = Math.min(pageNumber * pageSize, totalCount)

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('navigation.inventorySalesOrders')}</h1>
            <p className="text-muted-foreground text-sm">Sales Order Details</p>
          </div>
        </div>
        <Button onClick={() => navigate('/inventory/sales-orders/new')} className="w-full sm:w-auto shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          Add Order
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="w-full max-w-md">
              <label className="mb-2 block text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search (this page)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <TableColumnVisibilityMenu
                columns={SALES_ORDER_LIST_COLUMNS}
                visibility={columnVisibility}
                onToggle={toggleColumn}
                align="end"
                minVisible={1}
              />
              <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={loading}>
                <FileDown className="mr-2 h-4 w-4" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy} disabled={loading}>
                <Copy className="mr-2 h-4 w-4" />
                Copy
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                  {columnVisibility.code ? (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Code
                    </TableHead>
                  ) : null}
                  {columnVisibility.reference ? (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Reference
                    </TableHead>
                  ) : null}
                  {columnVisibility.warehouse ? (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Warehouse
                    </TableHead>
                  ) : null}
                  {columnVisibility.orderDate ? (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Order date
                    </TableHead>
                  ) : null}
                  {columnVisibility.expectedDeliveryDate ? (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Expected delivery date
                    </TableHead>
                  ) : null}
                  {columnVisibility.total ? (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Total
                    </TableHead>
                  ) : null}
                  {columnVisibility.edit ? (
                    <TableHead className="h-12 w-[88px] bg-primary/5 px-2 text-center text-xs font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Edit
                    </TableHead>
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {!branchId ? (
                  <TableRow>
                    <TableCell
                      colSpan={Math.max(visibleColumnCount, 1)}
                      className="py-12 text-center text-muted-foreground"
                    >
                      Select a branch to load sales orders.
                    </TableCell>
                  </TableRow>
                ) : loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={Math.max(visibleColumnCount, 1)}
                      className="py-12 text-center text-muted-foreground"
                    >
                      <div className="p-12 text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        <p className="mt-4 text-muted-foreground">Loading…</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={Math.max(visibleColumnCount, 1)}
                      className="py-12 text-center text-muted-foreground"
                    >
                      No data available in table
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((r) => (
                    <TableRow
                      key={r.id}
                      className="border-b transition-colors hover:bg-primary/5 dark:hover:bg-primary/10"
                    >
                      {columnVisibility.code ? (
                        <TableCell className="px-4 py-2 text-center text-sm">{r.code}</TableCell>
                      ) : null}
                      {columnVisibility.reference ? (
                        <TableCell className="px-4 py-2 text-center text-sm">{r.reference}</TableCell>
                      ) : null}
                      {columnVisibility.warehouse ? (
                        <TableCell className="px-4 py-2 text-center text-sm">{r.warehouse}</TableCell>
                      ) : null}
                      {columnVisibility.orderDate ? (
                        <TableCell className="px-4 py-2 text-center text-sm">{fmtDate(r.orderDate)}</TableCell>
                      ) : null}
                      {columnVisibility.expectedDeliveryDate ? (
                        <TableCell className="px-4 py-2 text-center text-sm">
                          {fmtDate(r.expectedDeliveryDate)}
                        </TableCell>
                      ) : null}
                      {columnVisibility.total ? (
                        <TableCell className="px-4 py-2 text-center text-sm">{fmtTotal(r.total)}</TableCell>
                      ) : null}
                      {columnVisibility.edit ? (
                        <TableCell className="px-2 py-2 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => navigate(`/inventory/sales-orders/${r.id}/edit`)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col items-center justify-between gap-4 border-t px-4 py-4 sm:flex-row sm:px-6">
            <p className="text-center text-sm text-muted-foreground sm:text-left">
              Showing {startIdx} to {endIdx} of {totalCount} entries
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
              <span className="text-sm text-muted-foreground">Per page</span>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v))
                  setPageNumber(1)
                }}
                disabled={!branchId || loading}
              >
                <SelectTrigger className="h-8 w-[72px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                disabled={!branchId || loading || pageNumber <= 1}
              >
                Prev
              </Button>
              <span className="min-w-[3.5rem] text-center text-sm font-medium">
                {pageNumber} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                disabled={!branchId || loading || pageNumber >= totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
